import React from 'react';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// Simple notification system without Chakra UI
class SimpleToaster {
  constructor() {
    this.toasts = [];
    this.listeners = [];
    this.nextId = 1;
  }

  create({ title, description, status = 'info', duration = 5000, isClosable = true }) {
    const toast = {
      id: this.nextId++,
      title,
      description,
      status,
      duration,
      isClosable,
      createdAt: Date.now()
    };

    this.toasts.push(toast);
    this.notifyListeners();

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        this.remove(toast.id);
      }, duration);
    }

    return toast.id;
  }

  remove(id) {
    this.toasts = this.toasts.filter(toast => toast.id !== id);
    this.notifyListeners();
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener(this.toasts));
  }
}

// Create a singleton toaster instance
export const toaster = new SimpleToaster();

// Status accent config
const STATUS_CONFIG = {
  success: {
    borderLeftColor: T.moss,
    iconColor: T.moss,
    titleColor: T.moss,
    bg: `${T.moss}10`,
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: '1px' }}>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  error: {
    borderLeftColor: T.capRed,
    iconColor: T.capRed,
    titleColor: T.capRed,
    bg: `${T.capRed}0E`,
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: '1px' }}>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 5v3.5M8 11h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  warning: {
    borderLeftColor: T.orange,
    iconColor: T.orange,
    titleColor: T.orangeDark,
    bg: `${T.orange}10`,
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: '1px' }}>
        <path d="M8 2L14 13H2L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M8 7v2.5M8 11.5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  info: {
    borderLeftColor: T.ink2,
    iconColor: T.ink2,
    titleColor: T.ink2,
    bg: `${T.ink2}0A`,
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: '1px' }}>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 7v4M8 5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
};

// ToasterComponent for rendering notifications
export const ToasterComponent = () => {
  const [toasts, setToasts] = React.useState([]);

  React.useEffect(() => {
    const unsubscribe = toaster.subscribe(setToasts);
    return unsubscribe;
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '16px',
      right: '16px',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      fontFamily: T.sans,
    }}>
      {toasts.map(toast => {
        const cfg = STATUS_CONFIG[toast.status] || STATUS_CONFIG.info;
        return (
          <div
            key={toast.id}
            style={{
              position: 'relative',
              maxWidth: '400px',
              width: '100%',
              padding: '13px 15px',
              borderRadius: T.r3,
              boxShadow: T.sh2,
              border: `1px solid ${T.rule}`,
              borderLeft: `3px solid ${cfg.borderLeftColor}`,
              backgroundColor: T.card,
              marginBottom: 0,
              transition: 'opacity 0.2s, transform 0.2s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              {/* Status icon */}
              <span style={{ color: cfg.iconColor }}>
                {cfg.icon}
              </span>

              <div style={{ flex: 1, minWidth: 0 }}>
                {toast.title && (
                  <div style={{
                    fontWeight: 700,
                    fontSize: '13px',
                    marginBottom: toast.description ? '3px' : 0,
                    color: cfg.titleColor,
                    fontFamily: T.sans,
                  }}>
                    {toast.title}
                  </div>
                )}
                {toast.description && (
                  <div style={{
                    fontSize: '13px',
                    color: T.inkMuted,
                    lineHeight: 1.45,
                    fontFamily: T.sans,
                  }}>
                    {toast.description}
                  </div>
                )}
              </div>

              {toast.isClosable && (
                <button
                  onClick={() => toaster.remove(toast.id)}
                  style={{
                    marginLeft: '4px',
                    flexShrink: 0,
                    opacity: 0.55,
                    transition: 'opacity 0.15s',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: T.inkFaint,
                    padding: '2px',
                    borderRadius: T.r2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.55'; }}
                >
                  <svg
                    style={{ width: '14px', height: '14px' }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
